import { DURATION_LIMITS, PROMOTION_SOURCES, SOURCE_PERIOD_DEFAULTS } from 'constants/promotionRequest';

const openingLabel = opening => opening.title || opening.custom_level || opening.level || opening.uuid;

export const openingOptions = (openings = []) =>
  openings.map(opening => ({ value: opening.uuid, label: openingLabel(opening) }));

export const mergeSelectOptions = (...optionGroups) => {
  const seen = new Set();
  return optionGroups.flat().filter(option => {
    if (!option || seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

export const officeOptions = (offices = []) => offices.map(office => ({ value: office.uuid, label: office.name }));

export const userOptions = (users = []) => users.map(user => ({ value: user.uuid, label: user.full_name }));

/* Team Lead notification recipients — service head + service team leads of the job. */
export const teamLeadOptions = jobData => {
  const teamLeads = [];
  const { service } = jobData;
  if (service?.service_head) {
    teamLeads.push({ value: service.service_head.uuid, label: service.service_head.full_name });
  }
  service?.team_leads?.forEach(tl => {
    if (tl.uuid !== service.service_head?.uuid) {
      teamLeads.push({ value: tl.uuid, label: tl.full_name });
    }
  });
  return teamLeads;
};

/* Fields to prefill from the prior opening + the offices available as search regions
   (search regions must be a subset of the prior opening offices — enforced by backend). */
export const priorOpeningPrefill = (opening, jobName) => {
  const offices = officeOptions(opening.offices || []);
  const description = [opening.technical_requirements, opening.technical_responsibilities].filter(Boolean).join('');
  const level = opening.custom_level || opening.level || '';
  const title = [level, jobName].filter(Boolean).join(' ');
  return {
    fields: [
      { name: 'request_title', value: title },
      { name: 'description', value: description },
      { name: 'description_initial', value: description },
      { name: 'search_regions', value: offices },
      ...(opening.technology_area
        ? [
            {
              name: 'technology_areas',
              value: [{ value: opening.technology_area.uuid, label: opening.technology_area.name }],
            },
          ]
        : []),
    ],
    officeOptions: offices,
  };
};

/* One promotion request is created per promotion source (bulk create). All requests
   share the "Main info"; only the source/job boards/period/duration differ per block. */
export const formValuesToPayloads = values => {
  const openings = (values.openings || []).map(row => row.opening?.value).filter(Boolean);
  const priorOpening =
    values.prior_opening && openings.includes(values.prior_opening) ? values.prior_opening : openings[0] || null;
  const openingOrder = priorOpening
    ? [priorOpening, ...openings.filter(opening => opening !== priorOpening)]
    : openings;

  const mainInfo = {
    openings,
    opening_order: openingOrder,
    prior_opening: priorOpening,
    request_title: values.request_title,
    description: values.description,
    search_regions: (values.search_regions || []).map(office => office.value),
    technology_areas: (values.technology_areas || []).map(area => area.value),
    recruiter_leads: (values.recruiter_leads || []).map(rl => rl.value),
    team_lead_notification_recipients: (values.team_lead_notification_recipients || []).map(user => user.value),
    translation_languages: (values.translation_languages || []).map(lang => lang.value),
    additional_requirements: values.additional_requirements || '',
  };

  return (values.sources || []).map(source => ({
    ...mainInfo,
    promotion_source: source.promotion_source?.value,
    platform: (source.platform || []).map(platform => platform.value),
    job_boards: (source.job_boards || []).map(board => board.value),
    promotion_period_type: source.promotion_period_type?.value,
    duration: Number(source.duration?.value),
  }));
};

/* Openings ordered by `opening_order` (prior first), falling back to API order. */
const orderedRequestOpenings = request => {
  const byId = {};
  (request.openings || []).forEach(opening => {
    byId[opening.uuid] = opening;
  });
  const order =
    request.opening_order && request.opening_order.length
      ? request.opening_order
      : (request.openings || []).map(opening => opening.uuid);
  return order.map(uuid => byId[uuid]).filter(Boolean);
};

export const requestOpeningOptions = request => openingOptions(orderedRequestOpenings(request));

/* Inverse of `formValuesToPayloads` (single source) — seeds the form when editing. */
export const promotionRequestToFormValues = (request, periodTypeLabels) => {
  const openings = orderedRequestOpenings(request).map(opening => ({
    opening: { value: opening.uuid, label: openingLabel(opening) },
  }));
  return {
    sources: [
      {
        promotion_source: {
          value: request.promotion_source,
          label: request.promotion_source,
        },
        platform: (request.platform || []).map(platform => ({ value: platform, label: platform })),
        job_boards: (request.job_boards || []).map(board => ({ value: board, label: board })),
        promotion_period_type: {
          value: request.promotion_period_type,
          label: periodTypeLabels[request.promotion_period_type] ?? request.promotion_period_type,
        },
        duration: { value: String(request.duration), label: String(request.duration) },
      },
    ],
    openings: openings.length ? openings : [{ opening: undefined }],
    prior_opening: request.prior_opening?.uuid,
    request_title: request.request_title || '',
    description: request.description || '',
    description_initial: request.description || '',
    search_regions: (request.search_regions || []).map(office => ({ value: office.uuid, label: office.name })),
    technology_areas: (request.technology_areas || []).map(area => ({ value: area.uuid, label: area.name })),
    translation_languages: (request.translation_languages || []).map(lang => ({ value: lang, label: lang })),
    additional_requirements: request.additional_requirements || '',
    additional_requirements_initial: request.additional_requirements || '',
    recruiter_leads: (request.recruiter_leads || []).map(rl => ({ value: rl.uuid, label: rl.full_name })),
    team_lead_notification_recipients: (request.team_lead_notification_recipients || []).map(user => ({
      value: user.uuid,
      label: user.full_name,
    })),
  };
};

const changedSourceEntries = changedSources => {
  if (Array.isArray(changedSources)) {
    return changedSources.map((block, index) => [index, block]);
  }
  return Object.entries(changedSources || {}).map(([key, block]) => [Number(key), block]);
};

/* Defaults apply only when that row's source (or period type) actually changed.
   Form.List `add()` sends every row in `changedValues`, which must not reset earlier periods. */
export const applySourceChangeDefaults = ({
  currentSources = [],
  previousSources = [],
  changedSources,
  periodTypeLabels,
}) => {
  const next = currentSources.map(block => ({ ...block }));
  let mutated = false;

  changedSourceEntries(changedSources).forEach(([index, block]) => {
    if (!block || !next[index]) return;

    if ('promotion_source' in block) {
      const source = block.promotion_source?.value;
      const previousSource = previousSources[index]?.promotion_source?.value;
      if (source !== previousSource) {
        const defaults = SOURCE_PERIOD_DEFAULTS[source];
        if (defaults) {
          next[index].promotion_period_type = {
            value: defaults.periodType,
            label: periodTypeLabels[defaults.periodType],
          };
          next[index].duration = { value: String(defaults.duration), label: String(defaults.duration) };
        }
        if (source !== PROMOTION_SOURCES.JOB_BOARDS) next[index].job_boards = [];
        if (source !== PROMOTION_SOURCES.PPC) next[index].platform = [];
        mutated = true;
      }
    }

    if ('promotion_period_type' in block) {
      const periodType = block.promotion_period_type?.value;
      const previousPeriodType = previousSources[index]?.promotion_period_type?.value;
      if (periodType !== previousPeriodType) {
        const { duration } = next[index];
        if (periodType && duration && Number(duration.value) > DURATION_LIMITS[periodType]) {
          next[index].duration = undefined;
          mutated = true;
        }
      }
    }
  });

  return { sources: next, mutated };
};

export const formValuesToPublicationPayload = values => ({
  publication_start_date: values.publication_start_date ? values.publication_start_date.format('YYYY-MM-DD') : null,
  publication_links: (values.publication_links || []).map(link => (link || '').trim()).filter(Boolean),
  publication_comment: values.publication_comment || '',
});

/* Main-info-only payload for the limited edit (Approved/Published/Updated). */
export const formValuesToMainInfoPayload = values => ({
  request_title: values.request_title,
  description: values.description,
  search_regions: (values.search_regions || []).map(office => office.value),
  technology_areas: (values.technology_areas || []).map(area => area.value),
  recruiter_leads: (values.recruiter_leads || []).map(rl => rl.value),
  team_lead_notification_recipients: (values.team_lead_notification_recipients || []).map(user => user.value),
  translation_languages: (values.translation_languages || []).map(lang => lang.value),
  additional_requirements: values.additional_requirements || '',
});
